// Iter160 — Broker + data setup (extracted from HowToPage per user spec).
// One-time onboarding task, kept separate from the repeating "how do I
// use this tool" workflow. All Pro framing stripped: broker linking is
// a capability available to any account with valid API creds — the tier
// gating happens on downstream features, not on setup itself.
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, KeyRound, Server, ShieldCheck } from "lucide-react";

const BROKERS_LIVE = [
  { name: "Tradier", desc: "OAuth or personal access token. Options-friendly, fast quotes." },
  { name: "Alpaca",  desc: "Personal API key + secret. Stocks + options-lite." },
];

const BROKERS_SOON = [
  { name: "Interactive Brokers", desc: "TWS Gateway integration — pipeline." },
  { name: "Charles Schwab",      desc: "OAuth flow — pipeline." },
];

export default function SetupPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="setup-page">
      <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
        Broker &amp; Data Setup
      </h1>
      <p className="text-sm mb-8" style={{ color: "var(--hoip-text-secondary)" }}>
        A one-time task. Once your broker is linked, MarkovLab routes live
        quotes, real option chains, and (where you explicitly enable it) order
        initiation through <i>your own</i> brokerage account. If you&apos;re
        looking for the daily usage guide instead,{" "}
        <Link to="/how-to" className="text-blue-400 hover:underline" data-testid="setup-howto-link">
          go to How to Use MarkovLab
        </Link>.
      </p>

      <section className="mb-8" data-testid="setup-supported-now">
        <div className="flex items-center gap-2 mb-3">
          <Server className="w-4 h-4 text-emerald-400" />
          <h2 className="text-base font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Supported now</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {BROKERS_LIVE.map(b => (
            <div key={b.name} className="border rounded-sm p-3"
              style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
              data-testid={`broker-live-${b.name}`}>
              <div className="text-sm font-bold" style={{ color: "var(--hoip-text-primary)" }}>{b.name}</div>
              <div className="text-xs mt-1" style={{ color: "var(--hoip-text-secondary)" }}>{b.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8" data-testid="setup-supported-soon">
        <div className="flex items-center gap-2 mb-3">
          <Server className="w-4 h-4 text-amber-400" />
          <h2 className="text-base font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>In the pipeline</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {BROKERS_SOON.map(b => (
            <div key={b.name} className="border rounded-sm p-3 opacity-60"
              style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
              data-testid={`broker-soon-${b.name}`}>
              <div className="text-sm font-bold" style={{ color: "var(--hoip-text-primary)" }}>{b.name}</div>
              <div className="text-xs mt-1" style={{ color: "var(--hoip-text-secondary)" }}>{b.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8" data-testid="setup-steps">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-blue-400" />
          <h2 className="text-base font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Four steps to link your broker</h2>
        </div>
        <ol className="list-decimal ml-6 text-sm leading-7"
          style={{ color: "var(--hoip-text-secondary)" }}>
          <li>Open your broker account (Tradier or Alpaca).</li>
          <li>Generate an API key / access token from your broker&apos;s developer settings.</li>
          <li>Paste it into <Link to="/profile?tab=brokers" className="text-blue-400 hover:underline" data-testid="setup-settings-link">Profile → Brokers</Link> — MarkovLab stores it encrypted, per-user.</li>
          <li>You stay in control: revoke the token from your broker at any time to instantly cut MarkovLab off.</li>
        </ol>
      </section>

      <section className="mb-6 border rounded-sm p-4"
        style={{ background: "rgba(59,130,246,0.06)", borderColor: "rgba(59,130,246,0.30)" }}
        data-testid="setup-safety-note">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-bold" style={{ color: "var(--hoip-text-primary)" }}>Safety guarantees</span>
        </div>
        <ul className="text-xs space-y-1 ml-1" style={{ color: "var(--hoip-text-secondary)" }}>
          <li>&bull; MarkovLab never receives your broker password — API keys only.</li>
          <li>&bull; Order initiation is off by default. You opt in explicitly, per broker.</li>
          <li>&bull; Keys are encrypted at rest, scoped per user, and never logged.</li>
          <li>&bull; Revoke or rotate at your broker&apos;s side any time — MarkovLab picks it up on the next request.</li>
        </ul>
      </section>

      <div className="mt-8">
        <Link to="/how-to"
          className="inline-flex items-center gap-2 text-sm text-blue-400 hover:underline"
          data-testid="setup-cta-howto">
          Once linked, follow the daily workflow <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
